/** @jsxImportSource @emotion/react */
import BigNumber from 'bignumber.js';
import {
  ConnectWallet,
  FormikTokenTextField,
  LabeledInlineContent,
  Spinner,
} from 'components';
import { VError } from 'errors';
import React, { useContext } from 'react';
import { useTranslation } from 'translation';
import { convertTokensToWei, convertWeiToTokens, getContractAddress } from 'utilities';
import type { TransactionReceipt } from 'web3-core';

import {
  useGetBalanceOf,
  useGetMintedVai,
  useRepayVai,
  useRepayUmi,
  useGetMintedUmi,
  GetMintedUmiOutput,
  useGetUserInfo,
  useGetClaimableRewards,
  useGetNetworkRewardDetails,

  useGetAllUsers,
  useGetDirectReferralCount,
  useGetReferrerInfo,
  useGetAllUsersInNetwork,
  useGetNetworkRewardRecordsCount,
  useGetNetworkRewardRecord,
  useGetNetworkRewardRecordDetailed,
  useGetNetworkRewardsSummary,
  RepayUmiOutput,
} from 'clients/api';
import { useGetNetworkRewardLogs } from 'hooks/useLogApi';
import { TOKENS } from 'constants/tokens';
import { AmountForm, AmountFormProps } from 'containers/AmountForms';
import { AuthContext } from 'context/AuthContext';
import { DisableLunaUstWarningContext } from 'context/DisableLunaUstWarning';
import useConvertWeiToReadableTokenString from 'hooks/useConvertWeiToReadableTokenString';
import useHandleTransactionMutation from 'hooks/useHandleTransactionMutation';

import { Typography } from '@mui/material';
import { useStyles } from '../styles';
import RepayFee from './RepayFee';
import TEST_IDS from './testIds';
import NetworkRewardBreakdown from './NetworkRewardBredown';

export interface RepayVaiUiProps {
  disabled: boolean;
  isRepayVaiLoading: boolean;
  repayVai: (amountWei: BigNumber) => Promise<RepayUmiOutput | undefined>;
  isInitialLoading: boolean;
  userBalanceWei?: BigNumber;
  userMinte?: GetMintedUmiOutput;
  userInfo?: any;
  claimableRewards?: any;
  networkRewardDetails?: any;
  networkStatistics?: any;
  directReferralCount?: any;
  referrerInfo?: any;
  allUsersInNetwork?: any;
  networkRewardLogs?: any;
  recordsCount?: any;
  rewardsSummary?: any;
  detailedRecords?: any[];
}

export const RepayVaiUi: React.FC<RepayVaiUiProps> = ({
  disabled,
  userBalanceWei,
  userMinte,
  userInfo,
  claimableRewards,
  isRepayVaiLoading,
  isInitialLoading,
  repayVai,
  networkRewardDetails,
  networkStatistics,
  directReferralCount,
  referrerInfo,
  allUsersInNetwork,
  networkRewardLogs,
  recordsCount,
  rewardsSummary,
  detailedRecords,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const handleTransactionMutation = useHandleTransactionMutation();
  const userMintedWei = userMinte ? userMinte?.balance : 0;
  const limitTokens = React.useMemo(() => {
    const limitWei =
      new BigNumber(userMintedWei) || new BigNumber(0);

    return convertWeiToTokens({ valueWei: limitWei, token: TOKENS.vai }).toFixed();
  }, [userBalanceWei?.toFixed(), userMintedWei]);

  // Convert minted wei into VAI
  const readableRepayableVai = useConvertWeiToReadableTokenString({
    valueWei: new BigNumber(userMintedWei),
    token: TOKENS.dl,
  });

  const hasRepayableVai = userMintedWei || false;


  // 新增的数据计算
  const userBalance = React.useMemo(() => userInfo ? new BigNumber(userInfo.balance).dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [userInfo]);
  const stakeAmount = React.useMemo(() => userInfo ? new BigNumber(userInfo.stake).dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [userInfo]);
  const totalRewards = React.useMemo(() => userInfo ? new BigNumber(userInfo.totalRewards).dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [userInfo]);
  const claimableRewardAmount = React.useMemo(() => claimableRewards ? claimableRewards.claimableRewards.dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [claimableRewards]);

  // 分离的动态和静态收益
  const dynamicRewardAmount = React.useMemo(() => claimableRewards?.dynamicRewards ? claimableRewards.dynamicRewards.dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [claimableRewards]);
  const staticRewardAmount = React.useMemo(() => claimableRewards?.staticRewards ? claimableRewards.staticRewards.dividedBy(new BigNumber(10).pow(18)) : new BigNumber(0), [claimableRewards]);

  // UI状态管理
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);
  const [expandedRecord, setExpandedRecord] = React.useState<number | null>(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [isNetworkBreakdownOpen, setIsNetworkBreakdownOpen] = React.useState(false);

  // 响应式监听
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 动画样式
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { background-position: -200px 0; }
        100% { background-position: calc(200px + 100%) 0; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      @keyframes fadeInScale {
        0% { opacity: 0; transform: scale(0.9); }
        100% { opacity: 1; transform: scale(1); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }
      .dashboard-card {
        animation: fadeInScale 0.6s ease-out;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .dashboard-card:hover {
        transform: translateY(-4px) scale(1.01);
        box-shadow: 0 12px 25px rgba(0,0,0,0.12) !important;
      }
      @media (max-width: 768px) {
        .dashboard-card:hover {
          transform: none;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
        }
      }
      .shimmer {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200px 100%;
        animation: shimmer 1.5s infinite;
      }
      .floating {
        animation: float 3s ease-in-out infinite;
      }
      .data-pulse {
        animation: pulse 2s infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  const onSubmit: AmountFormProps['onSubmit'] = async amountTokens => {
    const amountWei = convertTokensToWei({
      value: new BigNumber(amountTokens),
      token: TOKENS.dl,
    });

    const result = await repayVai(amountWei);
    if (result?.success) {
      // Handle success case
      return;
    }
    throw new Error(result?.error || 'Transaction failed');
  };

  return (
    <ConnectWallet message={t('mintRepayVai.repayVai.connectWallet')}>
      {isInitialLoading ? (
        <Spinner />
      ) : (
        <AmountForm onSubmit={onSubmit} css={styles.tabContentContainer}>
          {({ values }) => (
            <>
              {/* 现代化仪表板布局 */}
              <div
                style={{
                  padding: isMobile ? '8px' : '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  minHeight: '100vh',
                  fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >

                {/* 页面标题区域 */}
                <div
                  style={{
                    marginBottom: isMobile ? '12px' : '24px',
                    textAlign: 'center',
                    color: 'white',
                    padding: isMobile ? '8px' : '16px',
                  }}
                >
                  <Typography
                    variant="h1"
                    style={{
                      fontSize: isMobile ? '1.4rem' : '2.5rem',
                      fontWeight: '700',
                      marginBottom: isMobile ? '4px' : '8px',
                      background: 'linear-gradient(45deg, #fff, #e0e6ff)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: isMobile ? '1.3' : '1.2',
                    }}
                  >
                    <span role="img" aria-label="rocket">🚀</span> {t('mintRepayVai.repayVai.dashboard.title')}
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      opacity: 0.9,
                      fontWeight: '400',
                      fontSize: isMobile ? '0.9rem' : '1.1rem',
                      lineHeight: '1.4',
                    }}
                  >
                    {t('mintRepayVai.repayVai.dashboard.subtitle')}
                  </Typography>
                </div>

                {/* 顶部统计卡片网格 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: isMobile ? '12px' : '20px',
                    marginBottom: isMobile ? '16px' : '28px',
                  }}
                >

                  {/* 核心数据卡片 1: 账户资产 */}
                  <div
                    className="dashboard-card"
                    style={{
                      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                      borderRadius: isMobile ? '12px' : '20px',
                      padding: isMobile ? '16px' : '24px',
                      color: '#2C3E50',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: isMobile ? '0 4px 15px rgba(168, 237, 234, 0.3)' : '0 10px 30px rgba(168, 237, 234, 0.3)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                    onMouseEnter={() => !isMobile && setHoveredCard('assets')}
                    onMouseLeave={() => !isMobile && setHoveredCard(null)}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(255,255,255,0.3)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}
                    >
                      <span role="img" aria-label="money">💰</span> {t('mintRepayVai.repayVai.dashboard.assetsOverview')}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <Typography
                        variant="small1"
                        style={{
                          opacity: 0.8,
                          marginBottom: '8px',
                          fontWeight: '500',
                        }}
                      >
                        {t('mintRepayVai.repayVai.dashboard.totalAccountValue')}
                      </Typography>
                      <div
                        style={{
                          fontSize: '2.5rem',
                          fontWeight: '700',
                          marginBottom: '4px',
                        }}
                      >
                        {userBalance.plus(stakeAmount).toFixed(2)}
                      </div>
                      <Typography variant="tiny" style={{ opacity: 0.7 }}>
                        DL Token
                      </Typography>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: isMobile ? '8px' : '16px',
                      }}
                    >
                      <div
                        style={{
                          background: 'rgba(255,255,255,0.4)',
                          borderRadius: '12px',
                          padding: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h5" style={{ fontWeight: '600', marginBottom: '4px' }}>
                          {userBalance.toFixed(4)}
                      </Typography>
                        <Typography variant="tiny" style={{ opacity: 0.7 }}>
                          {t('mintRepayVai.repayVai.dashboard.availableBalance')}
                        </Typography>
                      </div>
                      <div
                        style={{
                          background: 'rgba(255,255,255,0.4)',
                          borderRadius: '12px',
                          padding: '16px',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h5" style={{ fontWeight: '600', marginBottom: '4px' }}>
                          {stakeAmount.toFixed(4)}
                        </Typography>
                        <Typography variant="tiny" style={{ opacity: 0.7 }}>
                          {t('mintRepayVai.repayVai.dashboard.stakedAmount')}
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* 底部附加信息面板 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: isMobile ? '12px' : '20px',
                    marginTop: isMobile ? '16px' : '24px',
                  }}
                >

                  {/* 奖励统计 */}
                  <div
                    className="dashboard-card"
                    style={{
                      background: 'rgba(255,255,255,0.9)',
                      borderRadius: isMobile ? '12px' : '20px',
                      padding: isMobile ? '16px' : '20px',
                      boxShadow: isMobile ? '0 4px 15px rgba(0,0,0,0.08)' : '0 8px 25px rgba(0,0,0,0.1)',
                      backdropFilter: 'blur(20px)',
                    }}
                  >
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          background: 'linear-gradient(45deg, #ffeaa7, #fdcb6e)',
                          borderRadius: '12px',
                        }}
                      >
                        <Typography variant="small2" style={{ fontWeight: '500' }}>
                          {t('mintRepayVai.repayVai.dashboard.totalRewards')}
                  </Typography>
                        <Typography variant="small1" style={{ fontWeight: '700' }}>
                          {totalRewards.toFixed(4)} DL
                  </Typography>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          background: 'linear-gradient(45deg, #81ecec, #74b9ff)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                        }}
                        onClick={() => setIsNetworkBreakdownOpen(true)}
                        onMouseEnter={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(129, 236, 236, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <Typography variant="small2" style={{ fontWeight: '500' }}>
                          <span role="img" aria-label="network">🌐</span> 动态收益(网络)
                          <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.8 }}>
                            点击查看组成详情 →
                          </span>
                      </Typography>
                        <Typography variant="small1" style={{ fontWeight: '700' }}>
                          {dynamicRewardAmount.toFixed(4)} DL
                      </Typography>
                    </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          background: 'linear-gradient(45deg, #a8edea, #fed6e3)',
                          borderRadius: '12px',
                        }}
                      >
                        <Typography variant="small2" style={{ fontWeight: '500' }}>
                          <span role="img" aria-label="static">⚡</span> 静态收益(质押)
                      </Typography>
                        <Typography variant="small1" style={{ fontWeight: '700' }}>
                          {staticRewardAmount.toFixed(4)} DL
                      </Typography>
                    </div>
                  </div>
                </div>
                </div>
                  {/* 核心数据卡片 2: 收益汇总 */}
                  <div
                    className="dashboard-card"
                    style={{
                      background: rewardsSummary ?
                        'linear-gradient(145deg, #667eea 0%, #764ba2 100%)' :
                        'linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%)',
                      borderRadius: isMobile ? '16px' : '24px',
                      padding: isMobile ? '20px' : '28px',
                      color: rewardsSummary ? 'white' : '#64748b',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: (() => {
                        if (rewardsSummary) {
                          return isMobile ? '0 8px 32px rgba(102, 126, 234, 0.25)' : '0 16px 48px rgba(102, 126, 234, 0.25)';
                        }
                        return isMobile ? '0 4px 16px rgba(0,0,0,0.05)' : '0 8px 24px rgba(0,0,0,0.08)';
                      })(),
                      border: rewardsSummary ? 'none' : '1px solid rgba(226, 232, 240, 0.6)',
                    }}
                    onMouseEnter={() => !isMobile && setHoveredCard('summary')}
                    onMouseLeave={() => !isMobile && setHoveredCard(null)}
                  >
                    {/* 装饰性背景 */}
                    {rewardsSummary && (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            top: '-50px',
                            right: '-50px',
                            width: '200px',
                            height: '200px',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                            borderRadius: '50%',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '-30px',
                            left: '-30px',
                            width: '120px',
                            height: '120px',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                            borderRadius: '50%',
                          }}
                        />
                      </>
                    )}

                    {rewardsSummary ? (
                      <>
                        {/* 标题区域 */}
                        <div style={{ marginBottom: '24px', position: 'relative' }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px',
                              }}
                            >
                              <span role="img" aria-label="chart" style={{ fontSize: '16px' }}>📊</span>
                            </div>
                            <Typography
                              variant="h4"
                              style={{
                                fontWeight: '700',
                                margin: 0,
                                background: 'linear-gradient(45deg, #ffffff, #f0f4f8)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              {t('mintRepayVai.repayVai.dashboard.networkRewardsSummary')}
                            </Typography>
                          </div>
                          <Typography
                            variant="tiny"
                            style={{
                              opacity: 0.8,
                              fontStyle: 'italic',
                              background: 'rgba(255,255,255,0.1)',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              display: 'inline-block',
                            }}
                          >
                            getNetworkRewardsSummary API
                          </Typography>
                        </div>

                        {/* 主要数据展示 - 总金额 */}
                        <div
                          style={{
                            background: 'rgba(255,255,255,0.15)',
                  borderRadius: '16px',
                            padding: '20px',
                            marginBottom: '20px',
                            textAlign: 'center',
                            border: '1px solid rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Typography variant="tiny" style={{ opacity: 0.9, marginBottom: '8px', fontWeight: '600' }}>
                            <span role="img" aria-label="money">💰</span> 网体收益总金额
                  </Typography>
                          <div
                            style={{
                              fontSize: isMobile ? '2.2rem' : '2.8rem',
                              fontWeight: '800',
                              marginBottom: '4px',
                              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {convertWeiToTokens({
                              valueWei: rewardsSummary.totalRewards || new BigNumber(0),
                              token: TOKENS.dl,
                            }).toFixed(2)}
                          </div>
                          <Typography variant="small2" style={{ opacity: 0.8, fontWeight: '500' }}>
                            DL Token
                  </Typography>
                        </div>

                        {/* 统计数据网格 */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                            gap: isMobile ? '12px' : '16px',
                            marginBottom: '20px',
                          }}
                        >
                          {/* 记录总数 */}
                          <div
                            style={{
                              background: 'rgba(99, 102, 241, 0.2)',
                              borderRadius: '12px',
                              padding: '16px',
                              textAlign: 'center',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                            }}
                          >
                            <div style={{ fontSize: '1.6rem', fontWeight: '700', marginBottom: '4px' }}>
                              {rewardsSummary.totalRecords || 0}
                            </div>
                            <Typography variant="tiny" style={{ opacity: 0.9, fontWeight: '600' }}>
                              总记录数
                    </Typography>
                  </div>

                          {/* 激活记录 */}
                          <div
                            style={{
                              background: 'rgba(16, 185, 129, 0.2)',
                              borderRadius: '12px',
                              padding: '16px',
                              textAlign: 'center',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                            }}
                          >
                            <div style={{ fontSize: '1.6rem', fontWeight: '700', marginBottom: '4px' }}>
                              {rewardsSummary.activeRewards || 0}
                            </div>
                            <Typography variant="tiny" style={{ opacity: 0.9, fontWeight: '600' }}>
                              激活记录
                      </Typography>
                          </div>

                          {/* 已领取金额 */}
                          <div
                            style={{
                              background: 'rgba(34, 197, 94, 0.2)',
                              borderRadius: '12px',
                              padding: '16px',
                              textAlign: 'center',
                              border: '1px solid rgba(34, 197, 94, 0.3)',
                            }}
                          >
                            <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: '700', marginBottom: '4px' }}>
                              {convertWeiToTokens({
                                valueWei: rewardsSummary.claimedRewards || new BigNumber(0),
                                token: TOKENS.dl,
                              }).toFixed(2)}
                            </div>
                            <Typography variant="tiny" style={{ opacity: 0.9, fontWeight: '600' }}>
                              已领取
                      </Typography>
                    </div>

                          {/* 可领取金额 */}
                          <div
                            style={{
                              background: 'rgba(245, 158, 11, 0.2)',
                              borderRadius: '12px',
                              padding: '16px',
                              textAlign: 'center',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                            }}
                          >
                            <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: '700', marginBottom: '4px' }}>
                              {convertWeiToTokens({
                                valueWei: rewardsSummary.pendingRewards || new BigNumber(0),
                                token: TOKENS.dl,
                              }).toFixed(2)}
                            </div>
                            <Typography variant="tiny" style={{ opacity: 0.9, fontWeight: '600' }}>
                              可领取
                      </Typography>
                          </div>
                        </div>

                        {/* 进度条 - 显示领取进度 */}
                        <div
                          style={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '16px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography variant="tiny" style={{ opacity: 0.9, fontWeight: '600' }}>
                              {t('mintRepayVai.repayVai.dashboard.claimProgress')}
                            </Typography>
                            <Typography variant="tiny" style={{ opacity: 0.8 }}>
                              {rewardsSummary.totalRewards && !rewardsSummary.totalRewards.isZero() ?
                                rewardsSummary.claimedRewards.dividedBy(rewardsSummary.totalRewards).multipliedBy(100).toFixed(1) :
                                '0.0'}%
                      </Typography>
                    </div>
                          <div
                            style={{
                              width: '100%',
                              height: '8px',
                              background: 'rgba(255,255,255,0.2)',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: rewardsSummary.totalRewards && !rewardsSummary.totalRewards.isZero() ?
                                  `${rewardsSummary.claimedRewards.dividedBy(rewardsSummary.totalRewards).multipliedBy(100).toFixed(1)}%` :
                                  '0%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease',
                              }}
                            />
                  </div>
                </div>

                        {/* 底部信息 */}
                        <div
                          style={{
                            background: 'rgba(255,255,255,0.08)',
                            borderRadius: '8px',
                            padding: '12px',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="tiny" style={{ opacity: 0.7, fontStyle: 'italic' }}>
                            <span role="img" aria-label="sync">🔄</span> 数据实时同步自智能合约
                          </Typography>
                        </div>
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div
                          style={{
                            width: '60px',
                            height: '60px',
                            border: '3px solid #e2e8f0',
                            borderTop: '3px solid #3b82f6',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px',
                          }}
                        />
                        <Typography variant="small2" style={{ color: '#64748b' }}>
                          {t('mintRepayVai.repayVai.dashboard.waitingForData')}
                        </Typography>
                      </div>
                    )}
                  </div>


                </div>

                {/* API #3: 📋 收益记录详情 (青色渐变) */}
                <div
                  className="dashboard-card"
                  style={{
                    background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                    borderRadius: isMobile ? '12px' : '20px',
                    padding: isMobile ? '16px' : '24px',
                    marginBottom: isMobile ? '16px' : '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: isMobile ? '0 4px 15px rgba(168, 237, 234, 0.3)' : '0 10px 30px rgba(168, 237, 234, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                  onMouseEnter={() => !isMobile && setHoveredCard('records-detail')}
                  onMouseLeave={() => !isMobile && setHoveredCard(null)}
                >
                  {/* 装饰性背景元素 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      width: '200px',
                      height: '200px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      borderRadius: '50%',
                      transform: 'translate(50%, -50%)',
                    }}
                  />
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <Typography
                        variant="h3"
                        style={{
                          color: '#2C3E50',
                          fontWeight: '600',
                        }}
                      >
                        {t('mintRepayVai.repayVai.dashboard.recordsDetail')}
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(255,255,255,0.4)',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        marginBottom: '24px',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    >
                      <Typography variant="small2" style={{ color: '#2C3E50', fontWeight: '600' }}>
                        <span role="img" aria-label="target">🎯</span> {t('mintRepayVai.repayVai.dashboard.recordsDetailDescription')}
                      </Typography>
                      <div
                        style={{
                          background: 'linear-gradient(45deg, #2C3E50, #34495e)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}
                      >
                        {t('mintRepayVai.repayVai.dashboard.totalRecordsCount', { count: recordsCount?.count || 0 })}
                      </div>
                    </div>
                  </div>

                  {detailedRecords && detailedRecords.length > 0 ? (
                    <>
                      <div
                        style={{
                          maxHeight: isMobile ? '300px' : '400px',
                          overflowY: 'auto',
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: isMobile ? '8px' : '12px',
                          padding: isMobile ? '12px' : '16px',
                          border: '1px solid rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                                                {detailedRecords.filter(record => record).map((record: any, index: number) => {
                            const data = record;
                          return (
                            <div
                              key={`reward-${data.timestamp || index}`}
                              style={{
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.8), rgba(255,255,255,0.6))',
                                borderRadius: isMobile ? '8px' : '12px',
                                padding: isMobile ? '12px' : '16px',
                                marginBottom: isMobile ? '8px' : '12px',
                                border: (() => {
                                  if (!data.active) return '2px solid #9ca3af'; // 灰色 - 暂停
                                  if (data.claimed) return '2px solid #22c55e'; // 绿色 - 已领取
                                  return '2px solid #f59e0b'; // 黄色 - 待领取
                                })(),
                                boxShadow: (() => {
                                  if (!data.active) return '0 6px 20px rgba(156, 163, 175, 0.2)'; // 灰色阴影
                                  if (data.claimed) return '0 6px 20px rgba(34, 197, 94, 0.2)'; // 绿色阴影
                                  return '0 6px 20px rgba(245, 158, 11, 0.2)'; // 黄色阴影
                                })(),
                                position: 'relative',
                                transition: 'all 0.3s ease',
                                cursor: isMobile ? 'default' : 'pointer',
                                overflow: 'hidden',
                                opacity: data.active ? 1 : 0.7, // 非活跃记录稍微透明
                              }}
                              onMouseEnter={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                                  if (!data.active) {
                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(156, 163, 175, 0.3)';
                                  } else if (data.claimed) {
                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(34, 197, 94, 0.3)';
                                  } else {
                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(245, 158, 11, 0.3)';
                                  }
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                  if (!data.active) {
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(156, 163, 175, 0.2)';
                                  } else if (data.claimed) {
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(34, 197, 94, 0.2)';
                                  } else {
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.2)';
                                  }
                                }
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '10px',
                                  right: '10px',
                                  background: (() => {
                                    if (!data.active) return '#9ca3af'; // 灰色 - 暂停
                                    if (data.claimed) return '#22c55e'; // 绿色 - 已领取
                                    return '#f59e0b'; // 黄色 - 待领取
                                  })(),
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '20px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                }}
                              >
                                {t('mintRepayVai.repayVai.dashboard.recordNumber', { number: index + 1 })}
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div>
                                  <Typography variant="h5" style={{ 
                                    color: data.active ? '#2C3E50' : '#6b7280', 
                                    fontWeight: 'bold', 
                                    marginBottom: '4px',
                                    textDecoration: data.active ? 'none' : 'line-through', // 暂停的记录显示删除线
                                  }}>
                                    {convertWeiToTokens({ valueWei: data.amount || new BigNumber(0), token: TOKENS.dl }).toFixed(6)} DL
                                    {!data.active && (
                                      <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                                        (暂停)
                                      </span>
                                    )}
                                  </Typography>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                        color: 'white',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      记录 #{index + 1}
                                    </div>
                                    <Typography variant="tiny" style={{ color: '#2C3E50', opacity: 0.7 }}>
                                      网络收益
                                    </Typography>
                                  </div>
                                  
                                  {/* 新增：显示已领取金额和每日释放金额 */}
                                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{
                                      background: 'rgba(34, 197, 94, 0.1)', 
                                      padding: '4px 8px', 
                                      borderRadius: '8px',
                                      border: '1px solid rgba(34, 197, 94, 0.3)'
                  }}>
                                      <Typography variant="tiny" style={{ color: '#166534', fontWeight: 'bold' }}>
                                        {t('mintRepayVai.repayVai.dashboard.claimedAmount')}: {convertWeiToTokens({ valueWei: data.claimedAmount || new BigNumber(0), token: TOKENS.dl }).toFixed(4)} DL
                    </Typography>
                                    </div>
                                    <div style={{ 
                                      background: 'rgba(59, 130, 246, 0.1)', 
                                      padding: '4px 8px', 
                                      borderRadius: '8px',
                                      border: '1px solid rgba(59, 130, 246, 0.3)'
                                    }}>
                                      <Typography variant="tiny" style={{ color: '#1e40af', fontWeight: 'bold' }}>
                                        {t('mintRepayVai.repayVai.dashboard.dailyAmount')}: {convertWeiToTokens({ valueWei: data.dailyAmount || new BigNumber(0), token: TOKENS.dl }).toFixed(4)} DL
                    </Typography>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: (() => {
                                      if (!data.active) return '#f3f4f6'; // 灰色背景 - 暂停
                                      if (data.claimed) return '#dcfce7'; // 绿色背景 - 已领取
                                      return '#fef3c7'; // 黄色背景 - 待领取
                                    })(),
                                    padding: '8px 12px',
                                    borderRadius: '20px',
                                    border: (() => {
                                      if (!data.active) return '2px solid #9ca3af'; // 灰色边框
                                      if (data.claimed) return '2px solid #22c55e'; // 绿色边框
                                      return '2px solid #f59e0b'; // 黄色边框
                                    })(),
                                  }}
                                >
                                  <Typography
                                    variant="tiny"
                                    style={{
                                      color: (() => {
                                        if (!data.active) return '#6b7280'; // 灰色文字
                                        if (data.claimed) return '#166534'; // 深绿色文字
                                        return '#92400e'; // 深黄色文字
                                      })(),
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {(() => {
                                      if (!data.active) return `⏸️ ${t('mintRepayVai.repayVai.dashboard.paused')}`;
                                      if (data.claimed) return `✅ ${t('mintRepayVai.repayVai.dashboard.claimed')}`;
                                      return `⏳ ${t('mintRepayVai.repayVai.dashboard.pending')}`;
                                    })()}
                                  </Typography>
                                </div>
                              </div>

                              <div
                                style={{
                                  background: 'rgba(255,255,255,0.5)',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  display: 'grid',
                                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                  gap: '12px',
                                }}
                              >
                                {/* 第一行：推荐人地址和开始时间 */}
                                <div>
                                  <Typography variant="tiny" style={{ color: '#2C3E50', opacity: 0.7, fontWeight: 'bold' }}>
                                    {t('mintRepayVai.repayVai.dashboard.referrerAddress')}
                        </Typography>
                                  <Typography variant="tiny" style={{ color: '#2C3E50', fontFamily: 'monospace' }}>
                                    {data.fromUser ? `${data.fromUser.slice(0, 8)}...${data.fromUser.slice(-6)}` : '--'}
                        </Typography>
                      </div>
                                <div>
                                  <Typography variant="tiny" style={{ color: '#2C3E50', opacity: 0.7, fontWeight: 'bold' }}>
                                    代数/所需推荐
                        </Typography>
                                  <Typography variant="tiny" style={{ color: '#2C3E50' }}>
                                    第{data.generation || 0}代 / 需{data.requiredReferrals || 0}人
                        </Typography>
                      </div>
                                
                                
                                {/* 第三行：释放进度和暂停时间 */}
                          
                                {data.pausedTime > 0 && (
                                  <div>
                                    <Typography variant="tiny" style={{ color: '#2C3E50', opacity: 0.7, fontWeight: 'bold' }}>
                                      暂停时间
                                    </Typography>
                                    <Typography variant="tiny" style={{ color: '#2C3E50' }}>
                                      {new Date(data.pausedTime * 1000).toLocaleString()}
                                    </Typography>
                  </div>
                )}
                              </div>
                              
                              {/* 进度条和可领取金额 */}
                              <div style={{
                                background: 'rgba(255,255,255,0.7)',
                                borderRadius: '8px',
                                padding: '12px',
                                marginTop: '8px'
                              }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <Typography variant="tiny" style={{ color: '#2C3E50', opacity: 0.7, fontWeight: 'bold' }}>
                                    {t('mintRepayVai.repayVai.dashboard.claimProgress')}
                                  </Typography>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                    <div style={{
                                      flex: 1,
                                      height: '8px',
                                      background: 'rgba(156, 163, 175, 0.3)',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                    }}>
                                      <div style={{
                                        width: data.totalDays > 0 ? `${Math.min((data.releasedDays / data.totalDays) * 100, 100).toFixed(1)}%` : '0%',
                                        height: '100%',
                                        background: data.active ? 
                                          (data.claimed ? '#22c55e' : '#f59e0b') : 
                                          '#9ca3af',
                                        borderRadius: '4px',
                                        transition: 'width 0.3s ease',
                                      }} />
                                    </div>
                                    <Typography variant="tiny" style={{ color: '#2C3E50', minWidth: '40px', fontWeight: 'bold' }}>
                                      {data.totalDays > 0 ? `${Math.min((data.releasedDays / data.totalDays) * 100, 100).toFixed(1)}%` : '0%'}
                                    </Typography>
                                  </div>
                                </div>
                                
                                <div style={{ 
                                  background: data.canRelease ? 'rgba(34, 197, 94, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: data.canRelease ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(156, 163, 175, 0.3)'
                                }}>
                                  <Typography variant="tiny" style={{ 
                                    color: data.canRelease ? '#166534' : '#6b7280', 
                                    fontWeight: 'bold' 
                                  }}>
                                    {t('mintRepayVai.repayVai.dashboard.pendingClaimable')}: {convertWeiToTokens({ 
                                      valueWei: data.claimableAmount || new BigNumber(0), 
                                      token: TOKENS.dl 
                                    }).toFixed(4)} DL
                                    {data.canRelease && ' ✅'}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '12px',
                        padding: '32px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h4" style={{ color: '#2C3E50', marginBottom: '12px' }}>
                        <span role="img" aria-label="search">🔍</span> {t('mintRepayVai.repayVai.dashboard.noRecordsTitle')}
                      </Typography>
                      <Typography variant="small2" style={{ color: '#2C3E50', opacity: 0.7 }}>
                        {t('mintRepayVai.repayVai.dashboard.noRecordsDescription')}
                      </Typography>
                      <div
                        style={{
                          marginTop: '16px',
                          background: 'rgba(255,255,255,0.4)',
                          borderRadius: '8px',
                          padding: '12px',
                        }}
                      >
                        <Typography variant="tiny" style={{ color: '#2C3E50', lineHeight: '1.4' }}>
                          <span role="img" aria-label="chart">📊</span> {t('mintRepayVai.repayVai.dashboard.currentStatus', { count: recordsCount?.count || 0 })}<br />
                          <span role="img" aria-label="refresh">🔄</span> {t('mintRepayVai.repayVai.dashboard.statusNote')}
                        </Typography>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: '16px',
                      background: 'rgba(255,255,255,0.4)',
                      borderRadius: '8px',
                      padding: '12px',
                    }}
                  >
                    <Typography variant="tiny" style={{ color: '#2C3E50', lineHeight: '1.4' }}>
                      <span role="img" aria-label="lightbulb">💡</span> <strong>{t('mintRepayVai.repayVai.dashboard.techDescription')}</strong>
                    </Typography>
                  </div>
                </div>

              </div>

              {/* 网络收益组成详情弹框 */}
              <NetworkRewardBreakdown
                open={isNetworkBreakdownOpen}
                onClose={() => setIsNetworkBreakdownOpen(false)}
                dynamicRewardAmount={dynamicRewardAmount}
                detailedRecords={detailedRecords || []}
              />
            </>
          )}
        </AmountForm>
      )}
    </ConnectWallet>
  );
};

const RepayVai: React.FC = () => {
  const { account } = useContext(AuthContext);

  const { hasLunaOrUstCollateralEnabled, openLunaUstWarningModal } = useContext(
    DisableLunaUstWarningContext,
  );

  const { data: userVaiBalanceData, isLoading: isGetUserVaiBalanceWeiLoading } = useGetBalanceOf(
    {
      accountAddress: account?.address || '',
      token: TOKENS.dl,
    },
    {
      enabled: !!account?.address,
    },
  );

  const { data: userMintedUmiData, isLoading: isGetUserMintedUmiLoading } = useGetMintedUmi(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  const { data: userInfoData, isLoading: isGetUserInfoLoading } = useGetUserInfo(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  const { data: claimableRewardsData, isLoading: isGetClaimableRewardsLoading } = useGetClaimableRewards(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  // 3. 网络收益详细分析 (使用现有API)
  const { data: networkRewardDetails, isLoading: isGetNetworkRewardDetailsLoading } = useGetNetworkRewardDetails(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  // 4. 网络统计信息 (已删除 - ABI中不存在此方法)

  // 5. 直推数量
  const { data: directReferralCount, isLoading: isGetDirectReferralCountLoading } = useGetDirectReferralCount(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  // 6. 推荐人信息
  const { data: referrerInfo, isLoading: isGetReferrerInfoLoading } = useGetReferrerInfo(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  // 7. 网络中的所有用户
  const { data: allUsersInNetwork, isLoading: isGetAllUsersInNetworkLoading } = useGetAllUsersInNetwork(
    {
      accountAddress: account?.address || '',
      maxLevels: 17,
    },
    {
      enabled: !!account?.address,
    },
  );

  // 8. 网络收益日志
  const networkRewardLogs = useGetNetworkRewardLogs({
    limit: 10,
    referrer: account?.address || '',
  });

  // 9. 新增的记录查询
  const { data: recordsCount } = useGetNetworkRewardRecordsCount(
    { accountAddress: account?.address || '' },
    { enabled: !!account?.address },
  );

  const { data: rewardsSummary } = useGetNetworkRewardsSummary(
    { accountAddress: account?.address || '' },
    { enabled: !!account?.address },
  );

  // 10. 获取更多收益记录详情 - 使用getNetworkRewardRecordDetailed获取详细信息
  const detailedRecord0 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 0 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 0 },
  );

  const detailedRecord1 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 1 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 1 },
  );

  const detailedRecord2 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 2 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 2 },
  );

  const detailedRecord3 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 3 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 3 },
  );

  const detailedRecord4 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 4 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 4 },
  );

  const detailedRecord5 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 5 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 5 },
  );

  const detailedRecord6 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 6 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 6 },
  );

  const detailedRecord7 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 7 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 7 },
  );

  const detailedRecord8 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 8 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 8 },
  );

  const detailedRecord9 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 9 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 9 },
  );

  const detailedRecord10 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 10 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 10 },
  );

  const detailedRecord11 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 11 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 11 },
  );

  const detailedRecord12 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 12 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 12 },
  );

  const detailedRecord13 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 13 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 13 },
  );

  const detailedRecord14 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 14 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 14 },
  );

  const detailedRecord15 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 15 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 15 },
  );

  const detailedRecord16 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 16 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 16 },
  );

  const detailedRecord17 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 17 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 17 },
  );

  const detailedRecord18 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 18 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 18 },
  );

  const detailedRecord19 = useGetNetworkRewardRecordDetailed(
    { accountAddress: account?.address || '', index: 19 },
    { enabled: !!account?.address && recordsCount && recordsCount.count > 19 },
  );

  // 组装详细记录数组，只包含有效数据
  const detailedRecords = React.useMemo(() => {
    const records = [
      detailedRecord0, detailedRecord1, detailedRecord2, detailedRecord3, detailedRecord4,
      detailedRecord5, detailedRecord6, detailedRecord7, detailedRecord8, detailedRecord9,
      detailedRecord10, detailedRecord11, detailedRecord12, detailedRecord13, detailedRecord14,
      detailedRecord15, detailedRecord16, detailedRecord17, detailedRecord18, detailedRecord19,
    ];
    return records.filter(record => record.data).map(record => record.data).slice(0, Math.min(recordsCount?.count || 0, 20));
  }, [
    detailedRecord0, detailedRecord1, detailedRecord2, detailedRecord3, detailedRecord4,
    detailedRecord5, detailedRecord6, detailedRecord7, detailedRecord8, detailedRecord9,
    detailedRecord10, detailedRecord11, detailedRecord12, detailedRecord13, detailedRecord14,
    detailedRecord15, detailedRecord16, detailedRecord17, detailedRecord18, detailedRecord19,
    recordsCount,
  ]);

  console.log('claimableRewardsData', claimableRewardsData?.claimableRewards.toString());
  console.log('networkRewardDetails', networkRewardDetails);
  // console.log('networkStatistics', networkStatistics); // 已删除
  console.log('directReferralCount', directReferralCount);
  console.log('referrerInfo', referrerInfo);
  console.log('allUsersInNetwork', allUsersInNetwork);
  console.log('networkRewardLogs', networkRewardLogs.data);
  console.log('recordsCount', recordsCount);
  console.log('rewardsSummary', rewardsSummary);
  console.log('detailedRecords', detailedRecords);

  const isInitialLoading = isGetUserMintedUmiLoading || isGetUserVaiBalanceWeiLoading || isGetUserInfoLoading || isGetClaimableRewardsLoading;

  const { mutateAsync: umiControllerContract, isLoading: isumiControllerContract } = useRepayUmi();

  const repayVai: RepayVaiUiProps['repayVai'] = async (amountWei) => {
    if (!account) {
      // This error should never happen, since the form inside the UI component
      // is disabled if there's no logged in account
      throw new VError({ type: 'unexpected', code: 'undefinedAccountErrorMessage' });
    }

    // Block action is user has LUNA or UST enabled as collateral
    if (hasLunaOrUstCollateralEnabled) {
      openLunaUstWarningModal();
      return;
    }

    return umiControllerContract({
      accountAddress: account.address,
      amountWei,
    });
  };

  return (
    <RepayVaiUi
      disabled={!account}
      isInitialLoading={isInitialLoading}
      userBalanceWei={userVaiBalanceData?.balanceWei}
      userMinte={userMintedUmiData}
      userInfo={userInfoData}
      claimableRewards={claimableRewardsData}
      isRepayVaiLoading={isumiControllerContract}
      repayVai={repayVai}
      networkRewardDetails={networkRewardDetails}
      networkStatistics={undefined}
      directReferralCount={directReferralCount}
      referrerInfo={referrerInfo}
      allUsersInNetwork={allUsersInNetwork}
      networkRewardLogs={networkRewardLogs}
      recordsCount={recordsCount}
      rewardsSummary={rewardsSummary}
      detailedRecords={detailedRecords}
    />
  );
};

export default RepayVai;
